import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'projects/workspace/src/environments/environment';
import { AppState } from '../../store/state/app.state';
import { selectCompanyProfile } from '../../administration/store/selectors';
import { CompanyProfile } from '../../administration/models/company-profile.model';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { ResponseModel } from '../../shared/models';
import {
  GeneralLedgerAccountModel,
  GeneralLedgerPlanModel,
  GeneralLedgerPlatformModel,
  GlGroupEnum,
  GlPlanModeEnum,
  GlProductTypeCategoryModel,
  GlProductTypeProductModel,
  GlRuleModel, GlTaxCodeModel
} from '../models';
import { DisplayToaster } from '../../shared/decorators/toaster';
import { GlMainNavService } from './gl-main-nav.service';
import { GlProductTypeProductCreateModel } from '../models/gl-product-type-product-create.model';
import { FamilyModel } from '../../../../../../common/src/modules/products';
import { ProductTypes } from '../../../../../../common/src/modules/products/product-types';

@Injectable()
export class GeneralLedgerApiService {

  private wid: number;

  private apiUrl(url: string = ''): string {
    return `${environment.javaApiVersion}/workspaces/${this.wid}/` + url;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly toasterService: ToasterService,
    private readonly translateService: TranslateService,
    private readonly store: Store<AppState>,
    private glMainNavService: GlMainNavService
  ) {
    this.store.select(selectCompanyProfile)
      .subscribe((profile: CompanyProfile) => {
        this.wid = profile.workspaceId;
      });
  }

  public getAccounts(viewMode: 'TREE'|'LIST' = 'TREE', planMode: GlPlanModeEnum = GlPlanModeEnum.ACTIVE): Observable<GeneralLedgerAccountModel[]> {
    const params = {
      viewMode,
      planMode,
    };
    return this.http.get<ResponseModel<GeneralLedgerAccountModel[]>>(this.apiUrl('general-ledger/accounts'), {params})
      .pipe(map((res: ResponseModel<GeneralLedgerAccountModel[]>) => res.data));
  }

  public getPlatforms(): Observable<GeneralLedgerPlatformModel[]> {
    const params = {
      viewMode: 'TREE',
      planMode: 'ACTIVE',
    };
    return this.http.get<ResponseModel<GeneralLedgerPlatformModel[]>>(this.apiUrl('general-ledger/platforms'), {params})
      .pipe(map((res: ResponseModel<GeneralLedgerPlatformModel[]>) => res.data));
  }

  public getPlan(planMode: GlPlanModeEnum = GlPlanModeEnum.ACTIVE): Observable<GeneralLedgerPlanModel> {
    const params = {
      mode: planMode,
    };
    return this.http.get<ResponseModel<GeneralLedgerPlanModel>>(this.apiUrl('general-ledger/plan'), {params})
      .pipe(map((res: ResponseModel<GeneralLedgerPlanModel>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public createAccount(account: {parentId?: number, code: string, name: string}): Observable<GeneralLedgerAccountModel[]> {
    return this.http.post<ResponseModel<GeneralLedgerAccountModel[]>>(this.apiUrl('general-ledger/accounts'), account)
      .pipe(map((res: ResponseModel<GeneralLedgerAccountModel[]>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public setEditMode(): Observable<GeneralLedgerAccountModel[]> {
    return this.http.get<ResponseModel<GeneralLedgerAccountModel[]>>(this.apiUrl('general-ledger/edit-plan'))
      .pipe(map((res: ResponseModel<GeneralLedgerAccountModel[]>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public commitEditMode(): Observable<boolean> {
    return this.http.request<ResponseModel<boolean>>('post', this.apiUrl('general-ledger/edit-plan'))
      .pipe(map((res: ResponseModel<boolean>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public resetEdited(): Observable<any> {
    return this.http.request<ResponseModel<any>>('post', this.apiUrl('general-ledger/reset'))
      .pipe(map((res: ResponseModel<any>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public selectChartsPlatform(
    selectedPlatformId: number,
    saveAsTemplate?: boolean,
    templateName?: string, // should be passed if saveAsTemplate=true
  ): Observable<any> {
    const body = {selectedPlatformId, saveAsTemplate, templateName}
    return this.http.post<ResponseModel<any>>(this.apiUrl('general-ledger/select-platform'), body)
      .pipe(map((res: ResponseModel<any>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public createChartsOfAccounts(
    accountingPlatformId: number,
    templateName: string,
    externalAccounts?: File,
  ): Observable<GeneralLedgerPlatformModel> {
    const formData = new FormData();
    if (accountingPlatformId) { formData.set('accountingPlatformId', accountingPlatformId.toString()); }
    if (externalAccounts) { formData.set('externalAccounts', externalAccounts); }
    if (templateName) { formData.set('templateName', templateName); }

    return this.http.post<ResponseModel<GeneralLedgerPlatformModel>>(this.apiUrl('general-ledger/import'), formData)
      .pipe(map((res: ResponseModel<GeneralLedgerPlatformModel>) => res.data));
  }

  public getRulesList(
    mode: GlPlanModeEnum,
    group: GlGroupEnum
  ): Observable<GlRuleModel[]> {
    const params = { mode, group };
    return this.http.get<ResponseModel<GlRuleModel[]>>(this.apiUrl('general-ledger/rules'), { params })
      .pipe(map((res: ResponseModel<GlRuleModel[]>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public createRule(rule: GlRuleModel, mode = GlPlanModeEnum.EDIT): Observable<GlRuleModel> {
    return this.http.post<ResponseModel<GlRuleModel>>(this.apiUrl('general-ledger/rules'), { ...rule, mode })
      .pipe(map((res: ResponseModel<GlRuleModel>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public updateRule(ruleId: number, rule: GlRuleModel): Observable<GlRuleModel> {
    return this.http.put<ResponseModel<GlRuleModel>>(this.apiUrl(`general-ledger/rules/${ruleId}`), { ...rule })
      .pipe(map((res: ResponseModel<GlRuleModel>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteRule(ruleId: number): Observable<null> {
    return this.http.delete<null>(this.apiUrl(`general-ledger/rules/${ruleId}`));
  }


  // PRODUCT TYPES
  public getProductTypeCategories(): Observable<GlProductTypeCategoryModel[]> {
    return this.http.get<ResponseModel<GlProductTypeCategoryModel[]>>(this.apiUrl('general-products/categories'))
      .pipe(
        map((response: ResponseModel<GlProductTypeCategoryModel[]>) => {
          // sort general categories by translated titles
          return {
            ...response,
            data: response.data
              .map(itm => ({
                ...itm,
                titleKey: itm.title,
                // custom categories keep the name entered by the user
                title: itm.type === 'CUSTOM'
                  ? itm.title
                  : this.translateService.instant('GL_CATEGORY.' + itm.title)
              }))
              .sort((a, b) => a.title.localeCompare(b.title))
          };
        }),
        map((res: ResponseModel<GlProductTypeCategoryModel[]>) => res.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public setCategoryVisibility(categoryId: number, value: boolean): Observable<any> {
    const params = { value: value.toString() };
    return this.http.post<ResponseModel<any>>(this.apiUrl(`general-products/categories/${categoryId}/set-visibility`), null, { params })
      .pipe(map((res: ResponseModel<any>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public createProductTypeCategory(category: { title: string; icon: string }): Observable<GlProductTypeCategoryModel[]> {
    return this.http.post<ResponseModel<GlProductTypeCategoryModel[]>>(this.apiUrl('general-products/categories'), category)
      .pipe(map((res: ResponseModel<GlProductTypeCategoryModel[]>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteProductTypeCategory(categoryId: number): Observable<GlProductTypeCategoryModel[]> {
    return this.http.delete<ResponseModel<GlProductTypeCategoryModel[]>>(this.apiUrl(`general-products/categories/${categoryId}`))
      .pipe(map((res: ResponseModel<GlProductTypeCategoryModel[]>) => res.data));
  }

  public getProductTypeProducts(mode: GlPlanModeEnum, familyId?: number): Observable<GlProductTypeProductModel[]> {
    const params = { mode };
    if (familyId) {
      params['familyId'] = familyId.toString();
    }

    return this.http.get<ResponseModel<GlProductTypeProductModel[]>>(this.apiUrl('general-products/items'), { params })
      .pipe(map((res: ResponseModel<GlProductTypeProductModel[]>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public createProductTypeProduct(product: GlProductTypeProductCreateModel, mode: GlPlanModeEnum): Observable<GlProductTypeProductModel> {
    const params = { mode };
    return this.http.post<ResponseModel<GlProductTypeProductModel>>(this.apiUrl('general-products/items'), { ...product }, { params })
      .pipe(map((res: ResponseModel<GlProductTypeProductModel>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public updateProductTypeProduct(
    productId: number,
    updateRequest: { fieldName: string; fieldValue: any }
  ): Observable<GlProductTypeProductModel> {
    const { fieldName, fieldValue } = updateRequest;
    const params = {
      fieldName,
      fieldValue: fieldValue === null || fieldValue === undefined ? '' : fieldValue.toString()
    };

    return this.http.patch<ResponseModel<GlProductTypeProductModel>>(this.apiUrl(`general-products/items/${productId}`), null, { params })
      .pipe(map((res: ResponseModel<GlProductTypeProductModel>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteProductTypeProduct(productId: number): Observable<null> {
    return this.http.delete<null>(this.apiUrl(`general-products/items/${productId}`));
  }
  // @END PRODUCT TYPES


  // EXPORT
  public getExportUrl(type: 'accounts' | 'rules'): string {
    return this.apiUrl(`gl/export/${type}`);
  }
  // @END EXPORT


  @DisplayToaster({showErrorMessage: true})
  public applyToPeriods(body: {
    periodType: string;
    year: number;
    quarter?: number;
    month?: number;
  }): Observable<{ totalDocuments: number; processedDocuments: number; failedDocuments: number }> {
    return this.http.post<ResponseModel<{ totalDocuments: number; processedDocuments: number; failedDocuments: number }>>(
      this.apiUrl('general-ledger/apply-to-periods'),
      body
    )
      .pipe(map((res: ResponseModel<{ totalDocuments: number; processedDocuments: number; failedDocuments: number }>) => res.data));
  }


  // TAX CODES
  public getTaxCodesList(
    mode: GlPlanModeEnum,
    group: GlGroupEnum
  ): Observable<GlTaxCodeModel[]> {
    const params = { mode, group };
    return this.http.get<ResponseModel<GlTaxCodeModel[]>>(this.apiUrl('general-ledger/tax-codes'), { params })
      .pipe(map((res: ResponseModel<GlTaxCodeModel[]>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public createTaxCode(taxCode: GlTaxCodeModel): Observable<GlTaxCodeModel> {
    return this.http.post<ResponseModel<GlTaxCodeModel>>(this.apiUrl('general-ledger/tax-codes'), { ...taxCode })
      .pipe(map((res: ResponseModel<GlTaxCodeModel>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public updateTaxCode(taxCodeId: string, taxCode: GlTaxCodeModel): Observable<GlTaxCodeModel> {
    return this.http.put<ResponseModel<GlTaxCodeModel>>(this.apiUrl(`general-ledger/tax-codes/${taxCodeId}`), { ...taxCode })
      .pipe(map((res: ResponseModel<GlTaxCodeModel>) => res.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public deleteTaxCode(taxCodeId: string): Observable<null> {
    return this.http.delete<null>(this.apiUrl(`general-ledger/tax-codes/${taxCodeId}`));
  }
  // @END TAX CODES


// delete the custom product
// DELETE {{path}}/accounting/v1/workspaces/:workspaceId/general-ledger/accounts/:accountId
//
// update the custom product
// PATCH {{path}}/accounting/v1/workspaces/:workspaceId/general-ledger/accounts/:accountId
// {
//   "code": "0110",
//   "name": "Patentrechte und Lizenzen (edited)"
// }
//

}
